import { useState, type FormEvent } from "react";
import { Plus, Smartphone, Trash2 } from "lucide-react";
import { startGoogleSession } from "../lib/auth";
import { updateVersionPolicy } from "../lib/api";
import { canRemoveAllowedVersion, MARKETING_VERSION_PATTERN, versionPolicyConfirmation } from "../lib/versionPolicy";
import { useTimeZone } from "../lib/TimeZoneContext";
import { formatTimestamp } from "../lib/timeZone";
import { GuardedChangeForm } from "./GuardedChangeForm";
import type { VersionPolicyControl, Viewer } from "../types";

interface PendingChange {
  action: "add" | "remove";
  version: string;
}

export function VersionPolicyPanel({ viewer, policy, loading, onChange }: {
  viewer: Viewer;
  policy: VersionPolicyControl | null;
  loading: boolean;
  onChange: (next: VersionPolicyControl) => void;
}) {
  const { timeZone } = useTimeZone();
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canChange = viewer.role === "superAdmin";
  const allowed = policy?.allowedVersions ?? [];
  const checking = Boolean(policy?.configured) && allowed.length > 0;
  const trimmed = draft.trim();
  const wellFormed = MARKETING_VERSION_PATTERN.test(trimmed);
  const duplicate = wellFormed && allowed.includes(trimmed);
  const canRemove = canRemoveAllowedVersion(allowed);
  const draftProblem = !trimmed || wellFormed
    ? duplicate ? `${trimmed} is already on the list.` : null
    : "Use numbers and dots only, like 1.0.4. Leave off the build number after the + sign.";

  const start = (change: PendingChange) => {
    setPending(change); setReason(""); setConfirmation(""); setError(null);
  };
  const cancel = () => { setPending(null); setReason(""); setConfirmation(""); setError(null); };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!policy || !pending) return;
    setSaving(true); setError(null);
    try {
      await startGoogleSession();
      onChange(await updateVersionPolicy({
        action: pending.action,
        version: pending.version,
        expectedRevision: policy.revision,
        reason: reason.trim(),
        confirmation
      }));
      setPending(null); setReason(""); setConfirmation(""); setDraft("");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "The version list could not be changed");
    } finally {
      setSaving(false);
    }
  };

  const impact = !pending
    ? null
    : pending.action === "remove"
      ? `Anyone still on ${pending.version} is asked to update before they can open the app again. The portal cannot yet count how many devices are on a version, so check with the team first.`
      : checking
        ? `${pending.version} joins the list. People on it can open the app straight away.`
        : `This is the first version on the list, so the check switches on. From that moment only ${pending.version} can open the app — every other build is asked to update.`;

  // Remounts per target so the confirm step re-announces and takes focus when
  // the operator switches from one version to another.
  const confirmForm = !pending ? null : (
    <GuardedChangeForm
      key={`${pending.action}-${pending.version}`}
      title={pending.action === "add" ? `Allow version ${pending.version}` : `Remove version ${pending.version}`}
      tone={pending.action === "remove" || !checking ? "danger" : undefined}
      impact={impact}
      phrase={versionPolicyConfirmation(pending.action, pending.version)}
      reason={reason}
      onReasonChange={setReason}
      reasonPlaceholder={pending.action === "add" ? "Why should this version be allowed?" : "Why is this version being blocked?"}
      confirmation={confirmation}
      onConfirmationChange={setConfirmation}
      requirements={[{ met: canChange, label: "super-admin access" }]}
      submitLabel={pending.action === "add" ? "Allow this version" : "Remove this version"}
      saving={saving}
      error={error}
      onCancel={cancel}
      onSubmit={(event) => void submit(event)}
    />
  );

  return (
    <section className="control-panel" aria-labelledby="versions-heading">
      <header className="control-panel-heading">
        <span className="control-panel-icon"><Smartphone size={20} aria-hidden="true" /></span>
        <div>
          <h2 id="versions-heading">Allowed app versions</h2>
          <p>Limit the app to specific released versions. Build numbers after the + sign are ignored.</p>
        </div>
        <span className={checking ? "control-state-pill is-healthy" : "control-state-pill is-warning"}>
          <span />{loading && !policy ? "Loading" : checking ? "Checking" : "No check"}
        </span>
      </header>
      <div className="control-panel-body">
        <p className="control-panel-state">
          {loading && !policy
            ? "Reading the current list…"
            : checking
              ? "Only the versions below can open the app. Anyone on a different version is asked to update first."
              : "The list is empty, so no version check runs and every build can open the app."}
        </p>
        <ul className="version-rows">
          {allowed.map((version) => (
            <li key={version} className={pending?.action === "remove" && pending.version === version ? "is-selected" : undefined}>
              <strong>{version}</strong>
              <button
                type="button"
                className="table-action"
                onClick={() => start({ action: "remove", version })}
                disabled={!canChange || !canRemove || saving}
                aria-label={`Remove version ${version}`}
                title={canRemove ? `Remove ${version}` : "Add another version before removing the last one"}
              >
                <Trash2 size={14} aria-hidden="true" />Remove
              </button>
            </li>
          ))}
          {!loading && allowed.length === 0 ? <li className="version-empty">No versions listed — the app is open to every build.</li> : null}
        </ul>
        {checking && !canRemove ? <p className="control-panel-note">The last version cannot be removed; that would lock everybody out. Add another one first.</p> : null}
        {pending?.action === "remove" ? confirmForm : null}
        <div className="stacked-field">
          <label htmlFor="version-add-input">Add a version</label>
          <div className="version-add-row">
            <input
              id="version-add-input"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="1.0.4"
              inputMode="decimal"
              autoComplete="off"
              disabled={!canChange || saving}
              aria-describedby="version-add-help"
            />
            <button
              type="button"
              className="secondary-button"
              disabled={!canChange || !wellFormed || duplicate || saving || !policy}
              onClick={() => start({ action: "add", version: trimmed })}
            >
              <Plus size={15} aria-hidden="true" />Add
            </button>
          </div>
          <small id="version-add-help" className={draftProblem ? "version-add-problem" : undefined}>
            {draftProblem ?? "The version people see in the app, such as 1.0.4."}
          </small>
        </div>
        {canChange ? null : <p className="control-panel-note">Only a super-admin can change this list.</p>}
        {pending?.action === "add" ? confirmForm : null}
        <p className="control-panel-footnote">
          Last changed {formatTimestamp(policy?.updatedAt ?? null, timeZone)} · change #{policy?.revision ?? 0}
        </p>
      </div>
    </section>
  );
}
